import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { IoArchiveOutline, IoCloseOutline, IoTrophy } from "react-icons/io5";
import ChampionshipBracket from "./ChampionshipBracket";

const ROUND_TITLES = {
  final: "GRAN FINAL",
  semifinal: "SEMIFINALES",
  quarterfinal: "CUARTOS DE FINAL",
  roundOf16: "OCTAVOS DE FINAL",
};
const ROUND_SHORT = { final: "F", semifinal: "SF", quarterfinal: "CF", roundOf16: "OF" };
const MODE_LABELS = { ciencia: "Ciencia", azar: "Azar", cienzar: "Cienzar", contrazar: "Contrazar" };

const formatDate = (value) => {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime()) || !date.getTime()) return "";
  return date.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Pirámide de un campeonato archivado, solo para mirar y ver repeticiones.
const ArchivedPyramid = ({ archive, onOpenReplay, renderSeat }) => {
  const rounds = useMemo(() => archive.rounds || [], [archive]);
  const roundKeyById = useMemo(() => {
    const map = {};
    rounds.forEach((round) => round.matches.forEach((match) => (map[match.id] = round.key)));
    return map;
  }, [rounds]);
  const seats = (rounds[0]?.matches || []).flatMap((match) => [match.a, match.b]);
  const label = (entry) => {
    if (!entry) return "—";
    if (entry.kind === "player") return entry.name;
    if (entry.kind === "bot") return "Bot XIMA";
    if (entry.kind === "none") return "Nadie";
    if (entry.kind === "winner") return `Gan. ${ROUND_SHORT[roundKeyById[entry.matchId]] || "R"} ${entry.index}`;
    return `Jugador ${entry.seat || ""}`.trim();
  };
  const renderMatch = (match, roundKey) => {
    const historyId = match.replayId || match.historyIds?.[match.historyIds.length - 1];
    const Tag = historyId ? "button" : "div";
    return (
      <Tag
        type={historyId ? "button" : undefined}
        onClick={historyId ? () => onOpenReplay(historyId, match) : undefined}
        className={`xc-match xc-round-${roundKey}${match.winner ? " is-done" : ""}${historyId ? " is-clickable is-replay" : ""}`}
      >
        {match.winner === "a" || match.winner === "b" ? (
          <>
            <em className="xc-winner-label">Ganador</em>
            <span className="is-winner xc-winner-name">{label(match[match.winner])}</span>
          </>
        ) : (
          <>
            <span>{label(match.a)}</span>
            <small>VS</small>
            <span>{label(match.b)}</span>
            <em className={match.winner === "none" ? "xc-none-line" : ""}>
              {match.winner === "none" ? "No se presentó ninguno" : "Sin jugar"}
            </em>
          </>
        )}
        {historyId ? <b className="xc-card-action is-replay">▶ Reproducir juego</b> : null}
      </Tag>
    );
  };
  return (
    <ChampionshipBracket
      rounds={rounds}
      seats={seats}
      roundTitles={ROUND_TITLES}
      renderMatch={renderMatch}
      renderSeat={renderSeat}
    />
  );
};

// Lista «Historial de campeonatos» y ventana para abrir cada uno.
const ChampionshipArchive = ({ refreshKey, onOpenReplay, renderSeat }) => {
  const [items, setItems] = useState([]);
  const [openArchive, setOpenArchive] = useState(null);
  const [loadingId, setLoadingId] = useState("");

  useEffect(() => {
    let canceled = false;
    axios
      .get("/championship/archive")
      .then((response) => {
        if (!canceled) setItems(Array.isArray(response?.data?.items) ? response.data.items : []);
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, [refreshKey]);

  const open = async (item) => {
    setLoadingId(String(item._id));
    try {
      const response = await axios.get(`/championship/archive/${item._id}`);
      setOpenArchive(response.data);
    } catch {
      // keep the list as it is
    } finally {
      setLoadingId("");
    }
  };

  return (
    <section className="xc-panel xc-archive">
      <div className="xc-panel-title">
        <IoArchiveOutline aria-hidden="true" />
        <span>HISTORIAL DE CAMPEONATOS</span>
      </div>
      {items.length ? (
        <div className="xc-archive-list">
          {items.map((item) => (
            <button
              key={item._id}
              type="button"
              className="xc-archive-item"
              onClick={() => open(item)}
              disabled={loadingId === String(item._id)}
            >
              <strong>Campeonato #{item.number}</strong>
              <span>{formatDate(item.finishedAt)}</span>
              <span>
                {item.playerCount} jugadores · {MODE_LABELS[item.gameMode] || item.gameMode}
              </span>
              <span className="xc-archive-champion">
                <IoTrophy aria-hidden="true" /> {item.champion?.name || "Sin campeón"}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="xc-empty">Todavía no hay campeonatos terminados.</p>
      )}

      {openArchive ? (
        <div className="xc-archive-overlay" role="dialog" aria-modal="true">
          <div className="xc-archive-dialog">
            <div className="xc-archive-head">
              <div>
                <h2>
                  {openArchive.title || "Campeonato XIMA"} #{openArchive.number}
                </h2>
                <p>
                  {formatDate(openArchive.finishedAt)} · {openArchive.playerCount} jugadores ·{" "}
                  {MODE_LABELS[openArchive.gameMode] || openArchive.gameMode} · {openArchive.timeMinutes} min
                  {openArchive.prize ? ` · Premio: ${openArchive.prize}` : ""}
                </p>
                <p className="xc-archive-champion">
                  <IoTrophy aria-hidden="true" /> Campeón: {openArchive.champion?.name || "Sin campeón"}
                </p>
              </div>
              <button type="button" onClick={() => setOpenArchive(null)} aria-label="Cerrar">
                <IoCloseOutline />
              </button>
            </div>
            <ArchivedPyramid archive={openArchive} onOpenReplay={onOpenReplay} renderSeat={renderSeat} />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ChampionshipArchive;
